import pmt from '@marcusthelin/pmt';
import { useEffect, useState } from 'react';
import { CalculatorStateType, HookProps } from '../types';

export const SESSION_STORAGE_KEY = 'brandedConsumerLoanCalculator';

export const useCalculator = ({ fixedRate, loanAmountDefault, tenorYearsDefault }: HookProps) => {
    const DEFAULT_STATE = {
        loanAmount: loanAmountDefault,
        tenorYears: tenorYearsDefault,
        showGovIdField: false,
    };

    const [calculatorState, setCalculatorState] = useState<CalculatorStateType>(DEFAULT_STATE);
    const [monthlyAmount, setMonthlyAmount] = useState<number | null>(null);

    const updateState = (key: string, value: number | string | boolean) => {
        setCalculatorState(prev => ({ ...prev, [key]: value }));
    };

    const formatNumber = (number: number) => number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '\u202F');

    const getMonthlyAmount = (loanAmount: number, tenorYears: number) => {
        if (isNaN(tenorYears) || isNaN(loanAmount)) {
            return null;
        }

        const monthlyRate = fixedRate / 100 / 12;
        const paybackPeriod = tenorYears * 12;

        const pmtAmount = pmt(monthlyRate, paybackPeriod, loanAmount, 0, 0, {
            positive: true,
            round: true,
        });

        return isNaN(pmtAmount) ? null : pmtAmount;
    };

    const getValuesFromStorage = () => {
        const session = sessionStorage?.getItem(SESSION_STORAGE_KEY);
        if (!session || typeof session !== 'string') {
            return null;
        }

        return JSON.parse(session) ?? null;
    };

    useEffect(() => {
        const parsedValues = getValuesFromStorage();
        if (parsedValues) {
            setCalculatorState({
                ...DEFAULT_STATE,
                ...parsedValues,
            });
        }
    }, []);

    useEffect(() => {
        sessionStorage.setItem(
            SESSION_STORAGE_KEY,
            JSON.stringify({
                loanAmount: calculatorState.loanAmount,
                tenorYears: calculatorState.tenorYears,
            })
        );
    }, [calculatorState]);

    useEffect(() => {
        const result = getMonthlyAmount(Number(calculatorState.loanAmount), Number(calculatorState.tenorYears));
        setMonthlyAmount(result);
    }, [calculatorState.loanAmount, calculatorState.tenorYears]);

    return {
        calculatorState,
        updateState,
        monthlyAmount,
        formatNumber,
    };
};
